/**
 * BrandedPartnerPortalPerSubNetworkPage — per-sub-network partner portal config.
 *
 * Accent: GREEN-only. Primary colour picker options are a curated green palette.
 * BE: POST /api/v1/branded-partner-portal-per-sub-network
 *     { network_id, brand_name, logo_url?, primary_color?, custom_domain?, support_email? }
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useBrandedPartnerPortalPerSubNetworkCreate,
	useBrandedPartnerPortalPerSubNetworkList,
} from "./hooks";
import { cnameHint, validateSubdomain, HEX_COLOR_RE, EMAIL_RE } from "./api";

// Plain Tailwind. ZINC borders, 6-8px rounding, GREEN accents ONLY.
const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-semibold uppercase tracking-wider text-zinc-600";

// GREEN-only primary accent palette (hex).
const GREEN_PALETTE: Array<{ name: string; hex: string }> = [
	{ name: "Lime", hex: "#65a30d" },
	{ name: "Leaf", hex: "#16a34a" },
	{ name: "Forest", hex: "#15803d" },
	{ name: "Pine", hex: "#166534" },
	{ name: "Mint", hex: "#4ade80" },
];

export function BrandedPartnerPortalPerSubNetworkPage() {
	const listQuery = useBrandedPartnerPortalPerSubNetworkList();
	const createMut = useBrandedPartnerPortalPerSubNetworkCreate();

	const [networkId, setNetworkId] = useState("");
	const [brandName, setBrandName] = useState("");
	const [logoUrl, setLogoUrl] = useState("");
	const [accent, setAccent] = useState(GREEN_PALETTE[1].hex);
	const [customDomain, setCustomDomain] = useState("");
	const [supportEmail, setSupportEmail] = useState("");

	const subCheck = useMemo(() => validateSubdomain(networkId), [networkId]);
	const emailValid = !supportEmail || EMAIL_RE.test(supportEmail);
	const hexValid = HEX_COLOR_RE.test(accent);
	const formValid =
		subCheck.ok && brandName.trim().length > 0 && emailValid && hexValid;

	const hint = useMemo(() => cnameHint(networkId, customDomain), [networkId, customDomain]);

	const items = listQuery.data?.items ?? [];

	const handlePublish = (e: FormEvent) => {
		e.preventDefault();
		if (!formValid) return;
		createMut.mutate({
			network_id: networkId.trim(),
			brand_name: brandName.trim(),
			logo_url: logoUrl.trim() || null,
			primary_color: accent,
			custom_domain: customDomain.trim() || null,
			support_email: supportEmail.trim() || null,
		});
	};

	return (
		<main
			data-testid="branded-partner-portal-per-sub-network-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className={LABEL}>branded-partner-portal-per-sub-network</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Branded partner portal per sub-network
				</h1>
				<p className="text-sm text-zinc-600">
					Configure the partner-facing portal for one sub-network. Each sub-network gets its
					own subdomain, logo, accent colour, and optional custom domain.
				</p>
			</header>

			<section className={CARD} aria-label="Portal configuration">
				<h2 className="text-base font-semibold text-zinc-900">Portal configuration</h2>
				<form
					onSubmit={handlePublish}
					className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2"
					data-testid="branded-partner-portal-per-sub-network-form"
				>
					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="bp-networkid">
							Sub-network subdomain
						</label>
						<input
							id="bp-networkid"
							type="text"
							className={INPUT}
							placeholder="acme-europe"
							value={networkId}
							onChange={(e) => setNetworkId(e.currentTarget.value.toLowerCase())}
							disabled={createMut.isPending}
							data-testid="branded-partner-portal-per-sub-network-networkid"
						/>
						<p className="text-xs text-zinc-500">
							<span data-testid="branded-partner-portal-per-sub-network-fqdn">
								{networkId ? `${networkId}.portal.trackerity.app` : "(subdomain).portal.trackerity.app"}
							</span>
						</p>
						{networkId && !subCheck.ok ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="branded-partner-portal-per-sub-network-subdomain-error"
							>
								{subCheck.error}
							</p>
						) : null}
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="bp-brand">
							Brand name
						</label>
						<input
							id="bp-brand"
							type="text"
							className={INPUT}
							placeholder="Acme Europe Partners"
							value={brandName}
							onChange={(e) => setBrandName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="branded-partner-portal-per-sub-network-brand"
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="bp-logo">
							Logo URL (upload hosted elsewhere)
						</label>
						<input
							id="bp-logo"
							type="url"
							className={INPUT}
							placeholder="https://cdn.example.com/logo.png"
							value={logoUrl}
							onChange={(e) => setLogoUrl(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="branded-partner-portal-per-sub-network-logo"
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="bp-email">
							Support email
						</label>
						<input
							id="bp-email"
							type="email"
							className={INPUT}
							placeholder="partners@example.com"
							value={supportEmail}
							onChange={(e) => setSupportEmail(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="branded-partner-portal-per-sub-network-email"
						/>
						{supportEmail && !emailValid ? (
							<p className="text-xs text-red-600" role="alert">
								Invalid email.
							</p>
						) : null}
					</div>

					<div className="flex flex-col gap-1.5 sm:col-span-2">
						<label className={LABEL}>Primary accent (green only)</label>
						<div
							className="flex flex-wrap gap-2"
							data-testid="branded-partner-portal-per-sub-network-palette"
						>
							{GREEN_PALETTE.map((c) => (
								<button
									key={c.hex}
									type="button"
									onClick={() => setAccent(c.hex)}
									aria-pressed={accent === c.hex}
									data-testid={`branded-partner-portal-per-sub-network-accent-${c.hex.slice(1)}`}
									className={`flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-xs font-medium ${
										accent === c.hex
											? "border-green-700 bg-green-50 text-green-800"
											: "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
									}`}
								>
									<span
										aria-hidden="true"
										className="h-3 w-3 rounded-full"
										style={{ backgroundColor: c.hex }}
									/>
									{c.name}
								</button>
							))}
						</div>
					</div>

					<div className="flex flex-col gap-1.5 sm:col-span-2">
						<label className={LABEL} htmlFor="bp-custom">
							Custom domain (optional)
						</label>
						<input
							id="bp-custom"
							type="text"
							className={INPUT}
							placeholder="partners.acme-europe.com"
							value={customDomain}
							onChange={(e) => setCustomDomain(e.currentTarget.value.toLowerCase())}
							disabled={createMut.isPending}
							data-testid="branded-partner-portal-per-sub-network-domain"
						/>
						{hint ? (
							<div
								className="mt-2 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3"
								data-testid="branded-partner-portal-per-sub-network-cname"
							>
								<p className={LABEL}>CNAME record</p>
								<code className="mt-1 block break-all font-mono text-xs text-zinc-800">
									{hint}
								</code>
							</div>
						) : null}
					</div>
				</form>
			</section>

			<section
				className={CARD}
				aria-label="Preview"
				data-testid="branded-partner-portal-per-sub-network-preview"
			>
				<h2 className="text-base font-semibold text-zinc-900">Preview</h2>
				<div
					className="mt-3 flex items-center justify-between rounded-[8px] border border-zinc-200 p-4"
					style={{ borderLeft: `4px solid ${accent}` }}
				>
					<div className="flex items-center gap-3">
						{logoUrl ? (
							<img
								src={logoUrl}
								alt="logo"
								className="h-10 w-10 rounded-[6px] border border-zinc-200 object-contain"
								data-testid="branded-partner-portal-per-sub-network-preview-logo"
							/>
						) : (
							<div
								className="flex h-10 w-10 items-center justify-center rounded-[6px] border border-zinc-300 bg-zinc-50 text-xs font-semibold text-zinc-500"
								aria-hidden="true"
							>
								LOGO
							</div>
						)}
						<div>
							<p
								className="text-sm font-semibold"
								style={{ color: accent }}
								data-testid="branded-partner-portal-per-sub-network-preview-brand"
							>
								{brandName || "Brand name"}
							</p>
							<p className="text-xs text-zinc-500">
								{customDomain || (networkId ? `${networkId}.portal.trackerity.app` : "subdomain.portal.trackerity.app")}
							</p>
						</div>
					</div>
					<span
						className="rounded-[6px] px-3 py-1 text-xs font-medium text-white"
						style={{ backgroundColor: accent }}
					>
						Partner portal
					</span>
				</div>
				<div className="mt-4 flex items-center justify-between">
					<p className="text-xs text-zinc-500">
						Publish to save config. Duplicate POSTs for the same sub-network are idempotent.
					</p>
					<button
						type="button"
						className={BTN}
						onClick={handlePublish}
						disabled={!formValid || createMut.isPending}
						data-testid="branded-partner-portal-per-sub-network-publish"
					>
						{createMut.isPending ? "Publishing." : "Publish"}
					</button>
				</div>
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="branded-partner-portal-per-sub-network-error"
					>
						{createMut.error.message}
					</p>
				) : null}
				{createMut.isSuccess ? (
					<p
						className="mt-2 text-xs text-green-700"
						role="status"
						data-testid="branded-partner-portal-per-sub-network-success"
					>
						Published.
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Configured sub-network portals">
				<h2 className="text-base font-semibold text-zinc-900">Configured sub-networks</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="branded-partner-portal-per-sub-network-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="branded-partner-portal-per-sub-network-empty"
					>
						No sub-network portals configured yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="branded-partner-portal-per-sub-network-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between py-3 text-sm"
								data-testid={`branded-partner-portal-per-sub-network-row-${row.network_id}`}
							>
								<div className="flex items-center gap-3">
									<span
										aria-hidden="true"
										className="h-3 w-3 rounded-full"
										style={{ backgroundColor: row.primary_color ?? "#16a34a" }}
									/>
									<div>
										<p className="font-medium text-zinc-900">{row.brand_name}</p>
										<p className="font-mono text-xs text-zinc-500">
											{row.network_id}.portal.trackerity.app
										</p>
									</div>
								</div>
								<span className="text-xs text-zinc-500">
									{row.custom_domain || "no custom domain"}
								</span>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default BrandedPartnerPortalPerSubNetworkPage;
